import React from "react";
import { CSSProperties, InputHTMLAttributes, KeyboardEventHandler, memo, ReactNode, useCallback, useState } from "react";
import { useMediaQuery, useOutsideClick } from "../../hooks";
import { IOption } from "../../types";

const AVAILABLE_KEYS = ["1", "2", "3", "4", "5", "6", "6", "7", "8", "9", "0", "Backspace", "Delete"];

const onKeyDownNumber: KeyboardEventHandler<HTMLDivElement> = (event) => {
  const value: string = (event.target as any).value;

  const hasDot = value?.includes(".");

  if (AVAILABLE_KEYS.includes(event.key) || (event.key === "." && !hasDot && value.length > 0)) {
    return;
  }

  event.preventDefault();
};

type Props = {
  inputValue: string | number;
  buttonTitle: (props: { isOpen: boolean }) => ReactNode;
  placeholder?: string;
  label?: string;
  onChange: (option: IOption) => void;
  onChangeInput: InputHTMLAttributes<HTMLInputElement>["onChange"];
  options: IOption<string, any>[];
  renderOption: (option: any, props: { onClick: () => void; onClose: () => void }) => ReactNode;
  renderDropDownHeader?: (props: { onClose: () => void }) => ReactNode;
  dropDownStyle?: CSSProperties;
};

const FlatExchangeSelectComponent = ({
  placeholder,
  label,
  buttonTitle,
  renderDropDownHeader,
  renderOption,
  options,
  inputValue,
  onChangeInput,
  dropDownStyle,
  onChange,
}: Props) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen((x) => !x);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const dropDownRef = useOutsideClick(handleClose);

  const handleRenderOption = useCallback(
    (v: IOption<string, any>) =>
      renderOption(v, {
        onClose: handleClose,
        onClick: () => {
          onChange(v);
          handleClose();
        },
      }),
    [onChange, renderOption, handleClose]
  );

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {label != null ? <span style={{ fontWeight: 500, fontSize: "15px", lineHeight: "145%", color: "#858585" }}>{label}</span> : null}

      <div style={{ display: "flex", height: "65px", overflow: "hidden", gap: "20px" }}>
        <input
          onKeyDown={onKeyDownNumber}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={onChangeInput}
          style={{
            border: "none",
            padding: "0 15px",
            fontSize: "20px",
            lineHeight: "165%",
            fontWeight: 400,
            width: '100%',
            borderRadius: 0,
            borderBottom: "1px solid #E3E3E3",
          }}
        />
        <button
          onClick={handleOpen}
          data-value="sell"
          style={{
            height: "100%",
            textAlign: "left",
            justifyContent: "flex-start",
            minWidth: "161px",
            maxWidth: "161px",
            border: "none",
            borderBottom: isOpen ? "3px solid #E41E1F" : "3px solid #F4F4F4",
            outline: "none",
            backgroundColor: "#F4F4F4",
          }}
        >
          <div className="d-flex justify-content-between gap-1 flex-1 align-items-center overflow-hidden" style={{ padding: "10px" }}>
            {buttonTitle({ isOpen }) ?? <div />}
            <div
              style={{
                minWidth: "8px",
                height: "5px",
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                background: "url(../img/dropdown-b.svg) 98% center no-repeat",
              }}
            />
          </div>
        </button>
      </div>

      {isOpen ? (
        <div
          ref={dropDownRef}
          style={{
            zIndex: 100,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            paddingTop: isMobile ? "70px" : "100px",
            cursor: "pointer",
          }}
          onClick={handleClose}
        >
          <div
            ref={dropDownRef}
            style={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#ffffff",
              overflow: "hidden",
              position: "relative",
              border: '1px solid #E3E3E3',
              maxHeight: "320px",
              ...dropDownStyle,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderDropDownHeader?.({ onClose: handleClose })}
            {options.map(handleRenderOption)}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const FlatExchangeSelect = memo(FlatExchangeSelectComponent);
